
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useClients } from '@/hooks/useClients';
import { useAvatarUpload } from '@/hooks/useAvatarUpload';
import { ProfileClientField } from './ProfileClientField';
import { useAuth } from '@/hooks/useAuth';

const profileSchema = z.object({
  full_name: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Email inválido'),
  phone: z.string().optional(),
  department: z.string().optional(),
  role: z.string().min(1, 'Role é obrigatória'),
  client_id: z.string().optional(),
  client_ids: z.array(z.string()).optional(),
  avatar_url: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  department: string | null;
  role: string;
  client_id: string | null;
  client_ids?: string[] | null;
  avatar_url: string | null;
}

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: Profile;
  onSuccess: () => void;
}

// Fixed Hooks order violation - v2
export const EditProfileDialog = ({ open, onOpenChange, profile, onSuccess }: EditProfileDialogProps) => {
  // useState hooks primeiro
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  
  // useRef
  const fileInputRef = useRef<HTMLInputElement>(null);

  // useForm (que usa hooks internamente)
  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: '',
      email: '',
      phone: '',
      department: '',
      role: 'user',
      client_id: '',
      avatar_url: '',
    },
  });
  
  // Hooks customizados sempre na mesma ordem
  const { toast } = useToast();
  const { uploadAvatar, isUploading } = useAvatarUpload();
  
  // useClients com verificação de estabilidade
  const clientsResult = useClients();
  const clients = clientsResult?.clients || [];
  const clientsLoading = clientsResult?.loading || false;

  // useAuth para verificar role do usuário atual
  const { profile: currentUserProfile } = useAuth();
  const isGlobalAdmin = currentUserProfile?.role === 'admin' || currentUserProfile?.role === 'super_admin';

  const availableRoles = isGlobalAdmin 
    ? [
        { value: 'admin', label: 'Admin Full' },
        { value: 'client_admin', label: 'Admin do Cliente' },
        { value: 'manager', label: 'Gerente' },
        { value: 'technician', label: 'Técnico' },
        { value: 'user', label: 'Usuário' },
      ]
    : [
        { value: 'client_admin', label: 'Admin do Cliente' },
        { value: 'manager', label: 'Gerente' },
        { value: 'technician', label: 'Técnico' },
        { value: 'user', label: 'Usuário' },
      ];

  // Todos os useEffect no final
  useEffect(() => {
    if (profile && open) {
      // Convert client_ids to array if it's a JSON string
      let clientIds: string[] = [];
      if (profile.client_ids) {
        if (Array.isArray(profile.client_ids)) {
          clientIds = profile.client_ids;
        } else if (typeof profile.client_ids === 'string') {
          try {
            clientIds = JSON.parse(profile.client_ids);
          } catch {
            clientIds = [];
          }
        }
      }
      // If no client_ids but has client_id, use single client
      else if (profile.client_id) {
        clientIds = [profile.client_id];
      }

      form.reset({
        full_name: profile.full_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        department: profile.department || '',
        role: profile.role || 'user',
        client_id: profile.client_id || '',
        client_ids: clientIds,
        avatar_url: profile.avatar_url || '',
      });
      setAvatarPreview(profile.avatar_url || null);
    }
  }, [profile, open, form]);

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Preview imediato
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload para Supabase
    const avatarUrl = await uploadAvatar(file, profile.id);
    if (avatarUrl) {
      form.setValue('avatar_url', avatarUrl);
    }
  };

  const onSubmit = async (data: ProfileFormData) => {
    try {
      // Determine client_id and client_ids
      let clientId = null;
      let clientIds = null;
      
      if (data.client_ids && data.client_ids.length > 0) {
        clientIds = data.client_ids;
        clientId = data.client_ids[0]; // Use first client as primary
      } else if (data.client_id) {
        clientId = data.client_id;
        clientIds = [data.client_id];
      }

      // Primeiro, tentar inserir ou atualizar na tabela profiles
      const { error: upsertError } = await supabase
        .from('profiles')
        .upsert({
          id: profile.id,
          full_name: data.full_name,
          email: data.email,
          phone: data.phone || null,
          department: data.department || null,
          role: data.role,
          client_id: clientId,
          client_ids: clientIds,
          avatar_url: data.avatar_url || null,
          updated_at: new Date().toISOString(),
        });

      if (upsertError) {
        console.error('Error upserting profile:', upsertError);
        toast({
          title: "Erro ao atualizar perfil",
          description: "Não foi possível atualizar o perfil: " + upsertError.message,
          variant: "destructive",
        });
        return;
      }

      // Também atualizar a tabela users para manter consistência
      const { error: userError } = await supabase
        .from('users')
        .update({
          name: data.full_name,
          email: data.email,
          role: data.role,
          client_id: clientId,
          client_ids: clientIds || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', profile.id);

      if (userError) {
        console.warn('Warning updating users table:', userError);
      }

      toast({
        title: "Perfil atualizado",
        description: "Perfil foi atualizado com sucesso.",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Erro ao atualizar perfil",
        description: "Erro inesperado ao atualizar perfil.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Perfil</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Avatar Upload Section */}
            <div className="flex flex-col items-center space-y-4 pb-4 border-b">
              <div className="relative">
                <Avatar className="h-24 w-24">
                  <AvatarImage 
                    src={avatarPreview || profile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.full_name || 'User')}&background=random`} 
                  />
                  <AvatarFallback>{profile?.full_name?.charAt(0) || 'U'}</AvatarFallback>
                </Avatar>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full p-0"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <Camera className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium">Foto de Perfil</p>
                <p className="text-xs text-muted-foreground">Clique no ícone da câmera para alterar</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>

            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Completo</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Departamento</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <ProfileClientField
              control={form.control}
              clients={clients}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={form.formState.isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Salvar Alterações
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
